import { RefObject, useEffect, useState } from 'react';

interface UseIntersectionObserverOptions {
  threshold?: number;
  rootMargin?: string;
  triggerOnce?: boolean;
  enabled?: boolean;
}

const useIntersectionObserver = <T extends HTMLElement>(
  ref: RefObject<T | null>,
  options: UseIntersectionObserverOptions = {}
): boolean => {
  const { threshold = 0.2, rootMargin = '0px 0px -50px 0px', triggerOnce = true, enabled = true } = options;

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!enabled || !ref.current) {
      return undefined;
    }

    const element = ref.current;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);

          if (triggerOnce) {
            observer.unobserve(element);
          }
        } else if (!triggerOnce) {
          setIsVisible(false);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, threshold, rootMargin, triggerOnce, enabled]);

  return isVisible;
};

export default useIntersectionObserver;
